import { forwardRef } from 'react';
import { Box, Skeleton, Stack } from '@mui/material';
import {
  MessagesList,
  MessagesListRef
} from 'services/conversations/components/MessagesList';
import { Message } from 'services/conversations/types';
import { useConversationDetailsTab } from 'modules/conversations/atoms/tabsAtom';
import { ConversationDetailsTabs } from 'modules/conversations/constants/conversationDetailsTabs';
import { MeetingNotes } from '../../calendar/components/MeetingNotes';
import { ConversationActions } from 'services/conversations/components/ConversationDetailsDrawer/ConversationActions';
import { useConversationActionState } from 'modules/conversations/hooks/useConversationActionState';
import { ConversationItem } from '../types/ConversationItem';
import { useFormik } from 'formik';
import { Channels } from 'generated-graphql/types';

type FormValues = {
  messageBody: string;
};

type ConversationMessagesListProps = {
  channel: Channels | null;
  isFetchingMessages: boolean;
  messagesList: Message[];
  conversation: ConversationItem;
  isDifferentConversationOngoing: boolean;
  isLoading: boolean;
  formik: ReturnType<typeof useFormik<FormValues>>;
  onCopyConversation: () => void;
  onUpdateConversation: (type: 'agent' | 'user') => Promise<void>;
  onRestartConversation: () => Promise<void>;
  flaggedMessageIds?: string[];
};

export const ConversationMessagesList = forwardRef<
  MessagesListRef,
  ConversationMessagesListProps
>(
  (
    {
      channel,
      isFetchingMessages,
      messagesList,
      conversation,
      isDifferentConversationOngoing,
      isLoading,
      formik,
      onCopyConversation,
      onUpdateConversation,
      onRestartConversation,
      flaggedMessageIds
    },
    ref
  ) => {
    const { selectedTab } = useConversationDetailsTab();

    const actionState = useConversationActionState({
      isDifferentConversationOngoing,
      conversation,
      channel: conversation.activeChannel || conversation.campaign?.channel,
      messageList: messagesList,
      campaignType: conversation.campaign?.type
    });

    if (
      selectedTab === ConversationDetailsTabs.MeetingNotes &&
      conversation.calendarBookingId
    ) {
      return (
        <MeetingNotes calendarBookingId={conversation.calendarBookingId} />
      );
    }

    return (
      <Stack display="flex" flexDirection="column" flexGrow={1} gap="25px">
        <Box display="flex" position="relative" flexGrow={1} minHeight="60vh">
          {isFetchingMessages ? (
            <Skeleton height="100%" width="100%" variant="rectangular" />
          ) : (
            <MessagesList
              ref={ref}
              messages={messagesList}
              agentDisplayName="Me"
              prospectDisplayName="Candidate"
              flaggedMessageIds={flaggedMessageIds}
            />
          )}
        </Box>
        <ConversationActions
          channel={channel}
          conversationId={conversation.id}
          isLoading={isLoading}
          state={actionState}
          formik={formik}
          onCopyConversation={onCopyConversation}
          onUpdateConversation={onUpdateConversation}
          onRestartConversation={onRestartConversation}
        />
      </Stack>
    );
  }
);
